import { formatDate } from 'date-fns';
import type { FieldValues, UseFormReturn } from 'react-hook-form';
import z from 'zod';
import type { ComboboxOption } from '../components';
import { COUNTRIES_WITH_CLASSIFICATION, COUNTRIES_WITH_DYNAMIC_PROBATION_MONTHS } from '../types';
import type {
  ConditionalDependsValueRules,
  CountriesWithClassification,
  CountriesWithDynamicProbationMonths,
  CountryEmploymentDocumentRequiredWhen,
  CountryField,
  CountryFieldDisabledDates,
  CountryFieldNote,
  CountryFieldsResponse,
} from '../types';
import {
  COUNTRY_FIELD_SCRIPTS,
  type CountryFieldScript,
  DATE_RANGE_TRANSFORM_SELF,
  DATE_RANGE_TRANSFORM_UNITS,
  DYNAMIC_FIELD_CALCULATION_TYPES,
  DYNAMIC_FIELD_CONDITIONAL_DEPENDS_CONDITIONS,
  DYNAMIC_FIELD_CONDITIONAL_REQUIRED_CONDITIONS,
  DYNAMIC_FIELD_TYPES,
  type DynamicCalculationType,
  EMPLOYEE_CONTRACT_TYPE,
} from './constants';
import {
  addMonths,
  type DateResolvableValue,
  getDayDifference,
  getFirstDayOfCurrentMonth,
  getFirstDayOfNextMonth,
  getMonthDifference,
  getNextWorkingDay,
  addYears,
  normalizeDate,
  resolveToDate,
  subtractMonths,
  subtractYears,
} from './date';
import { type NormalizeCaseRegistry, normalizeCaseMap } from './utils';

interface CountryFieldScriptRule {
  /** Full-value Zod / submit validation pattern. */
  pattern: RegExp;
  message: (label: string) => string;
}

/**
 * Charset rules for named `CountryField.script` presets.
 * `georgian_mkhedruli`: Mkhedruli U+10D0–U+10F0 (ა–ჰ), space, and hyphen.
 */
const COUNTRY_FIELD_SCRIPT_RULES: Record<CountryFieldScript, CountryFieldScriptRule> = {
  [COUNTRY_FIELD_SCRIPTS.GEORGIAN_MKHEDRULI]: {
    pattern: /^[\u10D0-\u10F0 -]+$/,
    message: (label) => `${label} must contain only Georgian characters, spaces, and hyphens`,
  },
};

export function getCountryFieldScriptRule(
  script: CountryFieldScript | undefined
): CountryFieldScriptRule | undefined {
  if (!script) return undefined;
  return COUNTRY_FIELD_SCRIPT_RULES[script];
}

/**
 * Converts a raw form value to the string key used in `value_rules` lookups.
 * Booleans → "1" / "0", numbers → their string, strings → trimmed.
 */
function getRuleLookupKey(value: FieldValues[string]) {
  const raw = value && typeof value === 'object' && 'value' in value ? value.value : value;

  if (typeof raw === 'boolean') return raw ? '1' : '0';
  if (typeof raw === 'number') return String(raw);
  if (typeof raw === 'string') return raw.trim();
  return undefined;
}

/** Compares two values using the same normalization as getRuleLookupKey so booleans,
 *  numbers, and strings are treated consistently across all equals checks. */
function isEqualsMatch(left: FieldValues[string], right: FieldValues[string]): boolean {
  const normLeft = getRuleLookupKey(left);
  const normRight = getRuleLookupKey(right);
  return normLeft !== undefined && normLeft === normRight;
}

/** Returns true when `value` matches any entry in `candidates`, using the same
 *  normalization as isEqualsMatch. Used by `condition: "in"` checks. */
function isInMatch(value: FieldValues[string], candidates: FieldValues[string][]): boolean {
  return candidates.some((candidate) => isEqualsMatch(value, candidate));
}

export function isDynamicProbationCountry(
  isoCode: string | undefined
): isoCode is CountriesWithDynamicProbationMonths {
  return COUNTRIES_WITH_DYNAMIC_PROBATION_MONTHS.includes(
    isoCode as CountriesWithDynamicProbationMonths
  );
}

export function isClassificationCountry(
  isoCode: string | undefined
): isoCode is CountriesWithClassification {
  return COUNTRIES_WITH_CLASSIFICATION.includes(isoCode as CountriesWithClassification);
}

/** Evaluates a field's `conditional_required` clause against the current form values. */
function isConditionalRequiredMet(
  conditional: NonNullable<CountryField['conditional_required']>,
  fieldValue: FieldValues[string]
): boolean {
  if (conditional.condition === DYNAMIC_FIELD_CONDITIONAL_REQUIRED_CONDITIONS.EQUALS) {
    return isEqualsMatch(fieldValue, conditional.value);
  }

  if (conditional.condition === DYNAMIC_FIELD_CONDITIONAL_REQUIRED_CONDITIONS.IN) {
    return isInMatch(fieldValue, conditional.values);
  }

  return true; // Default to showing the field
}

/**
 * Resolves the matching rule entry for a `condition: "equals"` block by looking up the
 * source field's value as a key. Falls back to `default` when no key matches.
 */
function resolveEqualsRule<T>(valueRules: Record<string, T>, ruleKey: string | undefined) {
  if (ruleKey !== undefined && Object.hasOwn(valueRules, ruleKey)) return valueRules[ruleKey];
  return valueRules.default;
}

/**
 * Resolves a threshold-based rule map by finding the first key whose numeric value
 * is >= diff. Falls back to `default` when no threshold is matched.
 */
function resolveRangeRule<T>(rules: Record<string, T>, diff: number) {
  const numericKeys = Object.keys(rules)
    .filter((k) => k !== 'default')
    .map(Number)
    .sort((a, b) => a - b);
  const matched = numericKeys.find((k) => diff <= k);
  return matched !== undefined ? rules[String(matched)] : rules.default;
}

/**
 * Creates a dynamic form schema by extending a base schema with country-specific fields
 * @param formSchema - The base Zod schema to extend
 * @param countryFields - Array of country-specific field configurations
 * @returns Extended Zod schema with dynamic country fields and conditional validation
 */
export function createDynamicFormSchema(
  formSchema: z.ZodObject<z.ZodRawShape>,
  countryFields?: CountryField[]
) {
  if (!countryFields || countryFields.length === 0) {
    return formSchema;
  }

  // In Zod v4, .refine() stores refinements on the ZodObject itself.
  // Calling .extend() to overwrite an existing key on a refined schema throws.
  // Skip dynamic fields whose names already exist in the base schema — those
  // fields are already validated by the base schema's own rules.
  const existingKeys = new Set(Object.keys(formSchema.shape));
  const newFields = countryFields.filter((f) => !existingKeys.has(f.name));

  // Even if there are no new fields to add we may still need to apply
  // conditional_required validation for fields already in the base schema
  // (handled by the superRefine below), so we continue rather than returning early.

  // Create dynamic fields for country-specific fields.
  // All fields are made optional at the schema level — type-level failures (e.g. z.date()
  // receiving undefined) do not set continue:true, which aborts the entire checks pipeline
  // in Zod v4 and prevents superRefine from running. Required validation is moved to
  // superRefine where errors are continuable by default.
  const countryFieldsSchema = newFields.reduce(
    (acc, field) => {
      let fieldSchema: z.ZodTypeAny;

      // Set base schema based on field type
      if (field.type === DYNAMIC_FIELD_TYPES.DATE) {
        const baseDate = z.preprocess(
          (val) => (val === '' ? undefined : val),
          z.date({ message: `${field.label} is required` })
        );
        fieldSchema = baseDate.optional().nullable();
      } else {
        fieldSchema = z.string();

        if (typeof field.max_length === 'number') {
          fieldSchema = (fieldSchema as z.ZodString).max(
            field.max_length,
            `${field.label} must be at most ${field.max_length} characters`
          );
        }

        if (typeof field.min_length === 'number') {
          fieldSchema = (fieldSchema as z.ZodString).min(
            field.min_length,
            `${field.label} must be at least ${field.min_length} characters`
          );
        }

        const scriptRule = getCountryFieldScriptRule(field.script);

        // `script` owns the charset when set — do not stack letters/uppercase on top.
        if (scriptRule) {
          fieldSchema = (fieldSchema as z.ZodString).regex(
            scriptRule.pattern,
            scriptRule.message(field.label)
          );
        } else if (field.digits && field.letters) {
          const alphanumericPattern = field.spaces_allowed
            ? /^[\p{L}\p{M}0-9\s]+$/u
            : /^[\p{L}\p{M}0-9]+$/u;
          fieldSchema = (fieldSchema as z.ZodString).regex(
            alphanumericPattern,
            `${field.label} must contain only letters and digits`
          );
        } else if (field.digits) {
          fieldSchema = (fieldSchema as z.ZodString).regex(
            /^[0-9]+$/,
            `${field.label} must contain only digits`
          );
        } else if (field.letters) {
          const lettersPattern = field.spaces_allowed ? /^[\p{L}\p{M}\s]+$/u : /^[\p{L}\p{M}]+$/u;
          fieldSchema = (fieldSchema as z.ZodString).regex(
            lettersPattern,
            `${field.label} must contain only letters`
          );
        }

        if (field.uppercase && !scriptRule) {
          fieldSchema = (fieldSchema as z.ZodString).regex(
            /^[^\p{Ll}]+$/u,
            `${field.label} must be uppercase`
          );
        }

        if (field.email) {
          fieldSchema = (fieldSchema as z.ZodString)
            .trim()
            .pipe(z.email(`${field.label} must be a valid email address`));
        }

        if (typeof field.min_value === 'number') {
          const minValue = field.min_value;
          fieldSchema = fieldSchema.refine(
            (val) =>
              typeof val !== 'string' ||
              val.trim() === '' ||
              Number.isNaN(Number(val)) ||
              Number(val) >= minValue,
            `${field.label} must be at least ${minValue}`
          );
        }

        if (typeof field.max_value === 'number') {
          const maxValue = field.max_value;
          fieldSchema = fieldSchema.refine(
            (val) =>
              typeof val !== 'string' ||
              val.trim() === '' ||
              Number.isNaN(Number(val)) ||
              Number(val) <= maxValue,
            `${field.label} must be at most ${maxValue}`
          );
        }

        fieldSchema = z.preprocess(
          (val) => (typeof val === 'string' && val.trim() === '' ? undefined : val),
          fieldSchema.optional()
        );
      }

      if (field.type === DYNAMIC_FIELD_TYPES.SELECT) {
        if (field.options && Object.keys(field.options).length > 0) {
          const optionKeys = Object.keys(field.options) as [string, ...string[]];
          // Option keys are always strings, but a field value can arrive as a number
          // (e.g. dynamic probation options are keyed by numeric months). Coerce to string
          // so the enum check compares like with like instead of rejecting `2` vs `"2"`.
          fieldSchema = z.preprocess(
            (val) => (val === '' || val === undefined || val === null ? undefined : String(val)),
            z.enum(optionKeys).optional()
          );
        } else {
          fieldSchema = z.preprocess(
            (val) => (val === '' ? undefined : val),
            z.string().optional()
          );
        }
      }

      acc[field.name] = fieldSchema;
      return acc;
    },
    {} as Record<string, z.ZodTypeAny>
  );

  // Only call .extend() when there are genuinely new fields to add
  const extendedSchema =
    Object.keys(countryFieldsSchema).length > 0
      ? formSchema.extend(countryFieldsSchema)
      : formSchema;

  // Required validation lives here because schema-level invalid_type errors lack
  // continue:true in Zod v4 and would abort the checks pipeline. Includes override
  // fields too — those are not in newFields (their name already exists in the base
  // schema), but the base only types them as `.optional()` and does not enforce
  // required-ness, so without this they'd silently pass when empty.
  const fieldsRequiredUnconditionally = countryFields.filter(
    (field) => field.required && !field.conditional_required
  );
  // Conditional validation covers all dynamic fields, including those that
  // already exist in the base schema (e.g. a dynamic endDate with conditional_required).
  const fieldsWithConditionalRequired = countryFields.filter((field) => field.conditional_required);
  const fieldsWithConditionalDepends = countryFields.filter((field) => field.conditional_depends);
  const fieldsWithScriptOnExistingKeys = countryFields.filter(
    (field) => existingKeys.has(field.name) && !!getCountryFieldScriptRule(field.script)
  );

  if (
    fieldsRequiredUnconditionally.length === 0 &&
    fieldsWithConditionalRequired.length === 0 &&
    fieldsWithConditionalDepends.length === 0 &&
    fieldsWithScriptOnExistingKeys.length === 0
  ) {
    return extendedSchema;
  }

  return extendedSchema.superRefine((formData, ctx) => {
    if (fieldsRequiredUnconditionally.length > 0) {
      fieldsRequiredUnconditionally.forEach((field) => {
        const fieldValue = formData[field.name];
        const isEmpty =
          field.type === DYNAMIC_FIELD_TYPES.DATE
            ? fieldValue === '' || fieldValue === undefined || fieldValue === null
            : !fieldValue || (typeof fieldValue === 'string' && fieldValue.trim().length === 0);

        if (isEmpty) {
          ctx.addIssue({
            code: 'custom',
            message: `${field.label} is required`,
            path: [field.name],
          });
        }
      });
    }

    if (fieldsWithScriptOnExistingKeys.length > 0) {
      fieldsWithScriptOnExistingKeys.forEach((field) => {
        const scriptRule = getCountryFieldScriptRule(field.script);
        if (!scriptRule) return;

        const fieldValue = formData[field.name];
        if (typeof fieldValue !== 'string' || fieldValue.trim().length === 0) return;

        if (!scriptRule.pattern.test(fieldValue)) {
          ctx.addIssue({
            code: 'custom',
            message: scriptRule.message(field.label),
            path: [field.name],
          });
        }
      });
    }

    if (fieldsWithConditionalRequired.length > 0) {
      // Conditional required fields
      fieldsWithConditionalRequired.forEach((field) => {
        const conditional = field.conditional_required!;

        if (
          isConditionalRequiredMet(conditional, formData[conditional.field] as FieldValues[string])
        ) {
          // If condition is met, the field becomes required
          const fieldValue = formData[field.name];

          // Handle different field types for validation
          let isFieldEmpty = false;

          if (field.type === DYNAMIC_FIELD_TYPES.DATE) {
            // For date fields, check if the value is undefined, null, or invalid date
            isFieldEmpty = fieldValue === '' || fieldValue === undefined || fieldValue === null;
          } else {
            // For text/select fields, check if empty string or undefined/null
            isFieldEmpty =
              !fieldValue || (typeof fieldValue === 'string' && fieldValue.trim().length === 0);
          }

          if (isFieldEmpty) {
            ctx.addIssue({
              code: 'custom',
              message: `${field.label} is required`,
              path: [field.name],
            });
          }
        }
      });
    }

    if (fieldsWithConditionalDepends.length > 0) {
      fieldsWithConditionalDepends.forEach((field) => {
        const conditional = field.conditional_depends!;
        if (conditional.condition !== DYNAMIC_FIELD_CONDITIONAL_DEPENDS_CONDITIONS.EQUALS) return;

        const sourceFieldName = conditional.fields[0];
        if (!sourceFieldName) return;

        // Only length-based `value_rules` are validated here; `select_value_rules` are
        // enforced at the UI level (option filtering + auto-clear in DynamicSelectField).
        if (!conditional.value_rules) return;

        const fieldRule = resolveEqualsRule(
          conditional.value_rules,
          getRuleLookupKey(formData[sourceFieldName])
        );
        if (!fieldRule) return;

        const fieldValue = formData[field.name];
        if (fieldValue === undefined || fieldValue === null || fieldValue === '') return;

        const len = String(fieldValue).length;

        if (typeof fieldRule.min_length === 'number' && len < fieldRule.min_length) {
          ctx.addIssue({
            code: 'custom',
            message: `${field.label} must be at least ${fieldRule.min_length} characters`,
            path: [field.name],
          });
        }

        if (typeof fieldRule.max_length === 'number' && len > fieldRule.max_length) {
          ctx.addIssue({
            code: 'custom',
            message: `${field.label} must be at most ${fieldRule.max_length} characters`,
            path: [field.name],
          });
        }
      });
    }
  });
}

/**
 * Determines if a dynamic field should be displayed based on conditional requirements
 * @param field - The country field configuration
 * @param form - React Hook Form instance to watch field values
 * @returns True if the field should be shown, false otherwise
 */
export function shouldShowField(field: CountryField, form: UseFormReturn) {
  if (!field.conditional_required) {
    return true; // Show field if no conditional requirements
  }

  const conditional = field.conditional_required;

  return isConditionalRequiredMet(conditional, form.watch(conditional.field));
}

function shouldShowFieldNote(fieldNote: CountryFieldNote, form: UseFormReturn) {
  const showWhen = fieldNote.show_when;

  if (showWhen?.condition === DYNAMIC_FIELD_CONDITIONAL_DEPENDS_CONDITIONS.IN) {
    const comparisonFieldValue = getRuleLookupKey(form.watch(showWhen.field));
    return (
      comparisonFieldValue !== undefined &&
      showWhen.values.some((value) => isEqualsMatch(comparisonFieldValue, value))
    );
  }

  return true;
}

/**
 * Determines whether a conditionally-required employment document should be treated as
 * required, based on the current form values. Returns false when there is no clause or
 * the watched field's value does not satisfy it.
 */
export function isEmploymentDocumentRequired(
  requiredWhen: CountryEmploymentDocumentRequiredWhen | undefined,
  formData: FieldValues
): boolean {
  if (!requiredWhen) return false;

  const fieldValue = formData[requiredWhen.field] as FieldValues[string];

  if (requiredWhen.condition === DYNAMIC_FIELD_CONDITIONAL_REQUIRED_CONDITIONS.EQUALS) {
    return isEqualsMatch(fieldValue, requiredWhen.value);
  }

  if (requiredWhen.condition === DYNAMIC_FIELD_CONDITIONAL_REQUIRED_CONDITIONS.IN) {
    return isInMatch(fieldValue, requiredWhen.value);
  }

  return false;
}

export function getCountryFieldNotes(
  fieldNotes: CountryFieldNote[] | undefined,
  fieldName: string,
  form: UseFormReturn
) {
  return (
    fieldNotes?.filter(
      (fieldNote) =>
        fieldNote.field === fieldName && !fieldNote.tooltip && shouldShowFieldNote(fieldNote, form)
    ) ?? []
  );
}

export function getCountryFieldTooltip(
  fieldNotes: CountryFieldNote[] | undefined,
  fieldName: string,
  form: UseFormReturn
) {
  return fieldNotes?.find(
    (fieldNote) =>
      fieldNote.field === fieldName &&
      Boolean(fieldNote.tooltip) &&
      shouldShowFieldNote(fieldNote, form)
  );
}

export const COUNTRY_FIELD_NOTE_NAMES = {
  JOB_ID: {
    PROFILE: 'employment_job_id',
    WIZARD: 'employmentJobId',
  },
} as const;

export type CountryFieldNoteJobFieldName =
  (typeof COUNTRY_FIELD_NOTE_NAMES.JOB_ID)[keyof typeof COUNTRY_FIELD_NOTE_NAMES.JOB_ID];

export interface RemapCountryFieldsResponseParams {
  countryFields: CountryFieldsResponse | null;
  jobFieldName: CountryFieldNoteJobFieldName;
  jobs: Array<{ id: number | string; key: string }>;
}

/**
 * Remaps job-dependent values from BE job keys (e.g. "software_engineer") to numeric
 * job ids (as strings, e.g. "42"), across every country in the response. Two shapes are
 * remapped:
 *   - field `conditional_required.values` for fields using the `in` condition
 *   - `field_notes[].show_when.values`
 * Only entries that reference `jobFieldName` are touched; everything else passes through
 * unchanged.
 *
 * Pure transform: fetching jobs is the caller's responsibility (see each app's
 * `getCountryFields` action). Field name normalization is handled separately by
 * {@link normalizeCountryFieldsResponse}. Idempotent — values that aren't known keys
 * (e.g. already-resolved ids) pass through unchanged.
 */
export function remapCountryFieldsResponse({
  countryFields,
  jobFieldName,
  jobs,
}: RemapCountryFieldsResponseParams): CountryFieldsResponse {
  if (!countryFields) return {};
  if (!jobs.length) return countryFields;

  const keyToId = new Map(jobs.map((job) => [job.key, String(job.id)]));
  const remapValue = (value: string) => keyToId.get(value) ?? value;

  return Object.entries(countryFields).reduce<CountryFieldsResponse>(
    (acc, [countryIsoCode, data]) => {
      acc[countryIsoCode] = {
        ...data,
        fields: data.fields.map((field) => {
          const conditional = field.conditional_required;
          if (
            !conditional ||
            conditional.condition !== DYNAMIC_FIELD_CONDITIONAL_REQUIRED_CONDITIONS.IN ||
            conditional.field !== jobFieldName
          ) {
            return field;
          }

          return {
            ...field,
            conditional_required: {
              ...conditional,
              values: conditional.values.map(remapValue),
            },
          };
        }),
        field_notes: data.field_notes?.map((note) => {
          if (note.show_when?.field !== jobFieldName) return note;

          return {
            ...note,
            show_when: {
              ...note.show_when,
              values: note.show_when.values.map(remapValue),
            },
          };
        }),
      };
      return acc;
    },
    {}
  );
}

/**
 * Computes the numeric range length for a field configured with `date_range_transform`.
 *
 * Returns the integer difference (in `transform.unit`) between `from_field` and
 * `to_field`. When `to_field === "self"`, `fieldValue` is used as the upper bound.
 * Returns `undefined` when the transform is absent or either anchor date cannot be
 * resolved (e.g. the field is hidden / the reference date is not yet selected).
 */
export function applyDateRangeTransform(
  field: CountryField,
  formData: FieldValues,
  fieldValue: Date
): number | undefined {
  const transform = field.date_range_transform;
  if (!transform) return undefined;

  const fromDate = resolveToDate(
    formData[transform.from_field] ||
      (formData[normalizeCaseMap['snakeToCamelCase'](transform.from_field)] as DateResolvableValue)
  );
  const toDate =
    transform.to_field === DATE_RANGE_TRANSFORM_SELF
      ? resolveToDate(fieldValue as DateResolvableValue)
      : resolveToDate(formData[transform.to_field] as DateResolvableValue);
  if (!fromDate || !toDate) return undefined;

  if (transform.unit === DATE_RANGE_TRANSFORM_UNITS.DAYS) return getDayDifference(fromDate, toDate);
  if (transform.unit === DATE_RANGE_TRANSFORM_UNITS.MONTHS)
    return getMonthDifference(fromDate, toDate);

  // YEARS: not yet needed — add getYearDifference when the first use-case arrives.
  return undefined;
}

/**
 * Builds a map of source field names to dependent dynamic field names.
 * Used when validating partial form saves: if a source field is dirty, its
 * dependents (via conditional_required.field or conditional_depends.fields)
 * must be validated too.
 */
export function buildCrossFieldDependencyMap(countryFields: CountryField[]): Map<string, string[]> {
  const map = new Map<string, string[]>();

  for (const field of countryFields) {
    const sources = new Set<string>();
    if (field.conditional_required?.field) sources.add(field.conditional_required.field);
    field.conditional_depends?.fields?.forEach((src) => sources.add(src));

    for (const src of sources) {
      const list = map.get(src) ?? [];
      if (!list.includes(field.name)) list.push(field.name);
      map.set(src, list);
    }
  }

  return map;
}

/**
 * Builds additional data object from dynamic country fields form data
 * @param countryFieldsForCountry - Array of country field configurations
 * @param formData - Form data containing field values
 * @returns Object with formatted field values for submission
 */
export function buildAdditionalData(
  countryFieldsForCountry: CountryField[],
  formData: FieldValues
) {
  const additionalData: Record<string, string> = {};

  // Add all dynamic country fields
  if (countryFieldsForCountry && formData) {
    countryFieldsForCountry.forEach((field) => {
      const fieldValue = formData[field.name as keyof typeof formData];
      if (fieldValue === undefined || fieldValue === null || fieldValue === '') {
        return;
      }

      if (fieldValue instanceof Date) {
        // date_range_transform: submit the numeric range length rather than the raw date.
        if (field.date_range_transform) {
          const rangeValue = applyDateRangeTransform(field, formData, fieldValue);
          if (rangeValue !== undefined) {
            additionalData[field.name] = String(rangeValue);
          }
        } else {
          additionalData[field.name] = formatDate(fieldValue, 'yyyy-MM-dd');
        }
      } else if (typeof fieldValue === 'object' && 'value' in fieldValue && fieldValue.value) {
        additionalData[field.name] = (fieldValue as ComboboxOption).value;
      } else {
        additionalData[field.name] = fieldValue.toString();
      }
    });
  }

  return additionalData;
}

// Value type used by dynamic fields in form state.
export type DynamicFormValue = string | Date | null | undefined;

// Map of dynamic field values keyed by country field name.
export type DynamicFormValues = Record<string, DynamicFormValue>;

export function getDefaultValuesForDynamicFields(fields: CountryField[], formData?: FieldValues) {
  return (
    fields.reduce<DynamicFormValues>((acc, field) => {
      const existing = formData?.[field.name];

      if (field.type === DYNAMIC_FIELD_TYPES.DATE) {
        if (field.date_range_transform && typeof existing === 'number') {
          // The backend stores the range length (e.g. 45 days) rather than a date.
          // Preserve it as a string so the component's hydrate effect can convert it
          // back to a Date once the reference date (from_field) becomes available.
          acc[field.name] = String(existing);
        } else {
          acc[field.name] = resolveToDate(existing);
        }
      } else {
        acc[field.name] = (existing as string) ?? '';
      }

      return acc;
    }, {}) || {}
  );
}

/**
 * Determines if a given end date should be disabled in the date picker.
 *
 * Business rules:
 * 1. End date must be after the start date
 * 2. For fixed contracts: End date must be at least 3 months after the start date (enforces 3-month minimum contract duration)
 *
 * @param date - The date to check
 * @param startDate - The selected start date (optional)
 * @param contractType - The contract type (permanent or fixed)
 * @returns true if the date should be disabled, false otherwise
 */
export function isEndDateDisabled(date: Date, startDate: Date | undefined, contractType: string) {
  if (!startDate) {
    return false;
  }

  const start = normalizeDate(startDate);
  const currentDate = normalizeDate(date);

  // For fixed contracts, enforce 3-month minimum duration
  if (contractType === EMPLOYEE_CONTRACT_TYPE.FIXED) {
    const threeMonthsAfterStart = addMonths(start, 3);
    // Disable dates before or equal to start date, and dates within 3 months after start
    return currentDate <= start || currentDate < threeMonthsAfterStart;
  }

  // For other contracts, just disable start date and any date before it
  return currentDate <= start;
}

/**
 * Determines if a given start date should be disabled in the date picker.
 *
 * Business rules:
 * 1. If the country allows retroactive employment: any date from the 1st of the current month onward
 * 2. If current day < 20th: Start date must be at least 5 working days from today (6th working day available)
 * 3. If current day >= 20th: Start date must be in the next month or later
 * 4. Start date cannot be after the end date (if end date is selected)
 * 5. For fixed contracts: Start date cannot be within 3 months before the end date (enforces 3-month minimum contract duration)
 *
 * @param date - The date to check
 * @param endDate - The selected end date (optional)
 * @param contractType - The contract type (permanent or fixed, optional)
 * @param allowsRetroactiveEmployment - Whether the country allows start dates from the 1st of the current month
 * @returns true if the date should be disabled, false otherwise
 */
export function isStartDateDisabled(
  date: Date,
  endDate?: Date,
  contractType?: string,
  allowsRetroactiveEmployment?: boolean
) {
  if (allowsRetroactiveEmployment) {
    if (normalizeDate(date) < getFirstDayOfCurrentMonth()) {
      return true;
    }
  } else {
    const today = normalizeDate(new Date());
    const currentDay = today.getDate();

    // Handle minimum start date based on current day of month
    if (currentDay < 20) {
      // If before 20th, first available day is today + 5 working days
      const nextWorkingDay = getNextWorkingDay();
      if (date < nextWorkingDay) {
        return true;
      }
    } else {
      // If 20th or later, start date must be in next month
      const firstDayNextMonth = getFirstDayOfNextMonth();
      if (date < firstDayNextMonth) {
        return true;
      }
    }
  }

  // Rule 3 & 4: Handle end date constraints
  if (endDate) {
    const end = new Date(endDate);
    end.setHours(0, 0, 0, 0);

    // For fixed contracts, enforce 3-month minimum duration
    if (contractType === EMPLOYEE_CONTRACT_TYPE.FIXED) {
      const threeMonthsBeforeEnd = subtractMonths(end, 3);
      // Disable dates that are after end date or too close to it (< 3 months gap)
      return date > end || date > threeMonthsBeforeEnd;
    }

    // For other contracts, just disable dates after end date
    return date > end;
  }

  return false;
}

/**
 * Normalised constraints produced by resolving a field's `conditional_depends` config
 * against the current form values. Components consume this instead of inspecting
 * `conditional_depends` directly.
 */
export interface ResolvedConstraints {
  /** Upper-bound date for DATE fields — derived from `date_value_rules`. */
  dependantMaxDate?: Date;
  /** Allowed option value keys for SELECT fields — derived from `select_value_rules`. */
  allowedOptionKeys?: string[];
  /** Active validation bounds for TEXT fields — derived from `value_rules`. */
  validationRules?: ConditionalDependsValueRules;
}

/**
 * Resolves a field's `conditional_depends` config against the current watched values,
 * returning a normalised `ResolvedConstraints` object.
 *
 * The component passes raw watched values (`dependsValues[i]` corresponds to
 * `field.conditional_depends.fields[i]`) and a `resolveDate` converter for the
 * date-range branch. Adding support for a new condition type means extending this
 * function only — no component changes are required.
 */
export function resolveFieldConstraints(
  field: CountryField,
  dependsValues: DateResolvableValue[],
  resolveDate: (val: DateResolvableValue) => Date | undefined
) {
  const result: ResolvedConstraints = {};
  const depends = field.conditional_depends;
  if (!depends) return result;

  if (depends.condition === DYNAMIC_FIELD_CONDITIONAL_DEPENDS_CONDITIONS.EQUALS) {
    if (depends.value_rules) {
      const rules = resolveEqualsRule(depends.value_rules, getRuleLookupKey(dependsValues[0]));
      if (rules) result.validationRules = rules;
    }

    if (depends.select_value_rules) {
      const allowedValues = resolveEqualsRule(
        depends.select_value_rules,
        getRuleLookupKey(dependsValues[0])
      );
      result.allowedOptionKeys = (allowedValues ?? []).map(String);
    }
  } else if (depends.condition === DYNAMIC_FIELD_CONDITIONAL_DEPENDS_CONDITIONS.LESS_THAN) {
    const fromDate = resolveDate(dependsValues[0]);
    const toDate = resolveDate(dependsValues[1]);

    if (!fromDate || !toDate) {
      const defaultSelectValues = depends.select_value_rules?.default;
      if (defaultSelectValues) {
        result.allowedOptionKeys = defaultSelectValues.map(String);
      }

      return result;
    }

    const diff =
      depends.unit === 'days'
        ? getDayDifference(fromDate, toDate)
        : getMonthDifference(fromDate, toDate);

    if (depends.date_value_rules) {
      const offsetDays = resolveRangeRule(depends.date_value_rules, diff);
      if (offsetDays !== undefined) {
        const maxDate = new Date(fromDate);
        maxDate.setDate(maxDate.getDate() + offsetDays);
        result.dependantMaxDate = maxDate;
      }
    }

    if (depends.select_value_rules) {
      const allowedValues = resolveRangeRule(depends.select_value_rules, diff);
      if (allowedValues) result.allowedOptionKeys = allowedValues.map(String);
    }
  }

  return result;
}

export interface DateConstraintContext {
  startDate?: Date;
  dependantMaxDate?: Date;
  minMonths?: number;
  maxMonths?: number;
  allowsRetroactiveEmployment?: boolean;
}

/**
 * Returns true if `date` falls outside the allowed range for a dynamic date
 * field constrained by a reference start date and optional month bounds.
 * Generic — used for end date, probation period end, and any future ranged
 * date fields.
 */
export function isDynamicDateDisabled(date: Date, ctx: DateConstraintContext) {
  const current = normalizeDate(date);
  const isAfterComputedMax = !!ctx.dependantMaxDate && current > ctx.dependantMaxDate;

  if (!ctx.startDate) return isAfterComputedMax;

  const start = normalizeDate(ctx.startDate);
  const minEnd = ctx.minMonths !== undefined ? addMonths(start, ctx.minMonths) : undefined;
  const maxEnd = ctx.maxMonths !== undefined ? addMonths(start, ctx.maxMonths) : undefined;
  const isBeforeMin = !!minEnd && current < minEnd;
  const isAfterMax = !!maxEnd && current > maxEnd;

  return isBeforeMin || isAfterComputedMax || isAfterMax;
}

function isNonNegativeInteger(value: number) {
  return Number.isInteger(value) && value >= 0;
}

/**
 * True when `disabled_dates` has at least one usable bound.
 * Rejects non-integer / negative values and inverted min/max ago ranges so
 * callers never install a broken disabled-date predicate.
 */
function hasDisabledDatesConstraints(constraints: CountryFieldDisabledDates) {
  const {
    min_years_ago: minYearsAgo,
    max_years_ago: maxYearsAgo,
    max_years_ahead: maxYearsAhead,
  } = constraints;

  const suppliedBounds = [minYearsAgo, maxYearsAgo, maxYearsAhead].filter(
    (bound) => bound !== undefined
  );

  if (suppliedBounds.length === 0) return false;
  if (!suppliedBounds.every(isNonNegativeInteger)) return false;

  if (minYearsAgo !== undefined && maxYearsAgo !== undefined && minYearsAgo > maxYearsAgo) {
    return false;
  }

  return true;
}

/**
 * Returns true if `date` falls outside absolute `disabled_dates` constraints
 * (rolling window from today).
 *
 * Past axis:   today - max_years_ago ≤ selected ≤ today - min_years_ago
 * Future axis: selected ≤ today + max_years_ahead
 */
export function isDisabledDatesRule(date: Date, constraints: CountryFieldDisabledDates): boolean {
  if (!hasDisabledDatesConstraints(constraints)) return false;

  const today = normalizeDate(new Date());
  const current = normalizeDate(date);

  if (constraints.min_years_ago !== undefined) {
    const latestAllowed = subtractYears(today, constraints.min_years_ago);
    if (current > latestAllowed) return true;
  }

  if (constraints.max_years_ago !== undefined) {
    const earliestAllowed = subtractYears(today, constraints.max_years_ago);
    if (current < earliestAllowed) return true;
  }

  if (constraints.max_years_ahead !== undefined) {
    const latestAllowed = addYears(today, constraints.max_years_ahead);
    if (current > latestAllowed) return true;
  }

  return false;
}

type IsDateDisabledFn = (date: Date, ctx?: DateConstraintContext) => boolean;

/**
 * Registry of field-name-specific disabled-date rules for dynamic date fields.
 * Reserved for bespoke business rules that cannot be expressed as `disabled_dates`
 * or `min_months`/`max_months` + `start_date_ref` (currently only `start_date`).
 */
export const DATE_FIELD_DISABLED_RULES: Record<string, IsDateDisabledFn> = {
  start_date: (date, ctx) =>
    isStartDateDisabled(date, undefined, undefined, ctx?.allowsRetroactiveEmployment),
};

/** Options for {@link getDisabledDate}. */
export interface GetDisabledDateOptions {
  /**
   * Keys matching `DATE_FIELD_DISABLED_RULES` (e.g. `start_date`) whose
   * bespoke rule should be skipped so callers can relax constraints (e.g. Admin Hub backdating).
   */
  skipNamedRulesForFields?: string[];
  /**
   * Absolute rolling-from-today constraints from the country field config (`disabled_dates`).
   */
  disabledDates?: CountryFieldDisabledDates;
}

/**
 * Resolves a disabled-date function for a dynamic date field.
 *
 * Resolution order:
 * 1. Field has a bespoke entry in DATE_FIELD_DISABLED_RULES (e.g. start_date)
 * 2. Field has `disabled_dates` constraints (e.g. birth_date min_years_ago,
 *    issuance_date max_years_ago + max_years_ahead)
 * 3. Field has config-driven range / dependant-max constraints → generic isDynamicDateDisabled
 *    (e.g. end_date, probation_period_end_date)
 * 4. No constraints → undefined (picker has no disabled dates)
 */
export function getDisabledDate(
  field: string,
  ctx: DateConstraintContext,
  options?: GetDisabledDateOptions
): IsDateDisabledFn | undefined {
  const namedRule = DATE_FIELD_DISABLED_RULES[field];
  const skipNamedRule = options?.skipNamedRulesForFields?.includes(field) ?? false;
  if (namedRule && !skipNamedRule) {
    return (date) => namedRule(date, ctx);
  }

  const agoConstraints = options?.disabledDates;
  if (agoConstraints && hasDisabledDatesConstraints(agoConstraints)) {
    return (date) => isDisabledDatesRule(date, agoConstraints);
  }

  const hasRange =
    ctx.startDate !== undefined && (ctx.minMonths !== undefined || ctx.maxMonths !== undefined);
  const hasDepends = ctx.dependantMaxDate !== undefined;

  if (hasRange || hasDepends) {
    return (date) => isDynamicDateDisabled(date, ctx);
  }

  return undefined;
}

export function getDefaultSelectedMonth(
  ctx: DateConstraintContext,
  disabledDates?: CountryFieldDisabledDates
) {
  if (ctx.startDate) return addMonths(ctx.startDate, ctx.minMonths ?? 1);

  if (disabledDates?.min_years_ago !== undefined) {
    return subtractYears(normalizeDate(new Date()), disabledDates.min_years_ago);
  }

  return undefined;
}

/**
 * Returns a new `CountryFieldsResponse` with all fields that have
 * `show_in_onboarding_only: true` removed. Use this for every consumer that
 * is not the add-employee onboarding wizard (TMP, employment, profiles, etc.).
 */
export function stripOnboardingOnlyFields(
  countryFields: CountryFieldsResponse | null
): CountryFieldsResponse {
  if (!countryFields) return {};
  return Object.entries(countryFields).reduce<CountryFieldsResponse>((acc, [iso, data]) => {
    acc[iso] = { ...data, fields: data.fields.filter((f) => !f.show_in_onboarding_only) };
    return acc;
  }, {});
}

export function dynamicFieldCalculate(
  startDate: Date,
  calculation: {
    type: DynamicCalculationType;
    years?: number;
    days?: number;
  }
) {
  const normalizedStartDate = normalizeDate(startDate);

  if (calculation.type === DYNAMIC_FIELD_CALCULATION_TYPES.ADD_YEARS_SUBTRACT_DAYS) {
    const calculatedDate = new Date(normalizedStartDate);
    calculatedDate.setFullYear(calculatedDate.getFullYear() + (calculation.years ?? 0));
    calculatedDate.setDate(calculatedDate.getDate() - (calculation.days ?? 0));

    return normalizeDate(calculatedDate);
  }

  return normalizedStartDate;
}

function getRawNonOverrideDynamicSiblings(siblingFields?: CountryField[]) {
  return new Set(
    (siblingFields ?? []).filter((sibling) => !sibling.override).map((sibling) => sibling.name)
  );
}

function normalizeCountryFieldReference(
  fieldName: string,
  normalizeCaseRegistry: NormalizeCaseRegistry,
  rawNonOverrideSiblings: Set<string>
) {
  return rawNonOverrideSiblings.has(fieldName)
    ? fieldName
    : normalizeCaseMap[normalizeCaseRegistry](fieldName);
}

function normalizeCountryField(
  field: CountryField,
  normalizeCaseRegistry: NormalizeCaseRegistry,
  siblingFields?: CountryField[]
): CountryField {
  const shouldNormalizeName = field.override ?? false;

  // Names of dynamic siblings without `override` keep their raw (snake_case) form keys,
  // so references to them in conditional config must not be case-normalized.
  // References to anything else (base form fields, override siblings) are normalized.
  const rawNonOverrideSiblings = getRawNonOverrideDynamicSiblings(siblingFields);

  const normalizedField: CountryField = {
    ...field,
    name: shouldNormalizeName ? normalizeCaseMap[normalizeCaseRegistry](field.name) : field.name,
  };

  if (field.start_date_ref) {
    normalizedField.start_date_ref = normalizeCaseMap[normalizeCaseRegistry](field.start_date_ref);
  }

  if (field.conditional_required) {
    normalizedField.conditional_required = {
      ...field.conditional_required,
      field: normalizeCountryFieldReference(
        field.conditional_required.field,
        normalizeCaseRegistry,
        rawNonOverrideSiblings
      ),
    };
  }

  if (field.conditional_depends) {
    normalizedField.conditional_depends = {
      ...field.conditional_depends,
      fields: field.conditional_depends.fields.map((ref) =>
        normalizeCountryFieldReference(ref, normalizeCaseRegistry, rawNonOverrideSiblings)
      ),
    };
  }

  if (field.date_range_transform) {
    normalizedField.date_range_transform = {
      ...field.date_range_transform,
      from_field: normalizeCaseMap[normalizeCaseRegistry](field.date_range_transform.from_field),
      // "self" is a sentinel — it refers to the field itself, not a sibling key, so skip normalisation.
      to_field:
        field.date_range_transform.to_field === DATE_RANGE_TRANSFORM_SELF
          ? field.date_range_transform.to_field
          : normalizeCaseMap[normalizeCaseRegistry](field.date_range_transform.to_field),
    };
  }

  return normalizedField;
}

function normalizeCountryFieldNote(
  fieldNote: CountryFieldNote,
  normalizeCaseRegistry: NormalizeCaseRegistry,
  siblingFields?: CountryField[]
): CountryFieldNote {
  const rawNonOverrideSiblings = getRawNonOverrideDynamicSiblings(siblingFields);

  return {
    ...fieldNote,
    field: normalizeCountryFieldReference(
      fieldNote.field,
      normalizeCaseRegistry,
      rawNonOverrideSiblings
    ),
    show_when: fieldNote.show_when
      ? {
          ...fieldNote.show_when,
          field: normalizeCountryFieldReference(
            fieldNote.show_when.field,
            normalizeCaseRegistry,
            rawNonOverrideSiblings
          ),
        }
      : undefined,
  };
}

export function normalizeCountryFieldsResponse(
  countryFields: CountryFieldsResponse | null,
  normalizeCaseRegistry: NormalizeCaseRegistry
): CountryFieldsResponse {
  if (!countryFields) {
    return {};
  }

  return Object.entries(countryFields).reduce<CountryFieldsResponse>(
    (acc, [countryIsoCode, data]) => {
      acc[countryIsoCode] = {
        ...data,
        fields: data.fields.map((field) =>
          normalizeCountryField(field, normalizeCaseRegistry, data.fields)
        ),
        field_notes: data.field_notes?.map((fieldNote) =>
          normalizeCountryFieldNote(fieldNote, normalizeCaseRegistry, data.fields)
        ),
      };
      return acc;
    },
    {}
  );
}
